import {
  calculateContributionMarginFromUnitPrice,
  calculateUnitPriceFromContributionMargin,
} from '@calculations/price/utilities/contribution-margin';
import { CalculationConfig } from '@calculations/types/calculation-config';
import { InitialMarginSource } from '@snap-types/models/snap-quote-line/columns';
import { TargetContributionMarginSource } from '@snap-types/models/snap-quote-price/price-calculation-results';
import { Decimal } from '@snap-types/types/decimal';
import type { Nullish } from '@snap-types/types/nullish';

export type TargetContributionMarginLastWin = {
  contributionMargin: Decimal;
  unitPrice: Decimal;
  quantity: Decimal;
};

export type TargetContributionMarginCalculateParams = {
  quantity: Decimal;
  unitVariableCosts: Decimal;
  targetContributionMargin: Decimal;
  lastWin?: Nullish<TargetContributionMarginLastWin>;
};

export type TargetContributionMarginCalculateResult = {
  quantity: Decimal;
  unitVariableCosts: Decimal;
  unitPrice: Decimal;
  contributionMargin: Decimal;
  contributionMarginSource: TargetContributionMarginSource;
};

export function calculate(
  {
    quantity,
    unitVariableCosts,
    targetContributionMargin,
    lastWin,
  }: TargetContributionMarginCalculateParams,
  config: CalculationConfig,
): TargetContributionMarginCalculateResult {
  let contributionMargin = targetContributionMargin;
  let contributionMarginSource: TargetContributionMarginSource =
    InitialMarginSource.targetContributionMargin;

  if (lastWin && lastWin.contributionMargin.gte(targetContributionMargin)) {
    contributionMargin = lastWin.contributionMargin;
    contributionMarginSource = InitialMarginSource.lastWin;
  }

  let { unitPrice } = calculateUnitPriceFromContributionMargin(
    {
      unitVariableCosts,
      contributionMargin,
    },
    config,
  );

  if (lastWinPriceAndQuantityGreater({ lastWin, unitPrice, quantity })) {
    // Use the last win unit price, calculating the contribution margin from it
    unitPrice = lastWin!.unitPrice;

    contributionMargin = calculateContributionMarginFromUnitPrice(
      { unitVariableCosts, unitPrice },
      config,
    );

    contributionMarginSource =
      InitialMarginSource.calculatedFromLastWinUnitPrice;
  }

  return {
    contributionMargin,
    contributionMarginSource,
    quantity,
    unitVariableCosts,
    unitPrice,
  };
}

function lastWinPriceAndQuantityGreater({
  lastWin,
  unitPrice,
  quantity,
}: {
  lastWin?: TargetContributionMarginLastWin | null;
  unitPrice: Decimal;
  quantity: Decimal;
}) {
  return (
    lastWin &&
    lastWin.unitPrice.gte(unitPrice) &&
    lastWin.quantity.gte(quantity)
  );
}
